<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import type { ProductSpec, QuotaSpec } from '@/api/ls'
import { type AddonQuotaInputMeta, addonQuotaInputMeta, addonStepError } from '@/lib/addonQuotaInput'
import { quotaAddonStep, quotaAllowsUnlimited, quotaRequired } from '@/lib/quotas'

/**
 * 數量配額編輯器（批65；批93 改為**資料驅動**；批114 吸收所有獨立數字欄）。
 *
 * 欄位清單、中文名、缺席語意（`absent_default`）與說明（`hint`）全部來自
 * **該產品的憑證設計**（`props.spec.quotas`）——批93 之前是 `lib/quotas.ts` 的
 * `QUOTA_KEYS`／`QUOTA_LABELS` 兩個寫死常數，只服務得了 CS 一個產品。
 *
 * v-model 綁 quotas dict（`Record<string, number | null>`）：
 * - 值 `null`＝**無上限**（明確的勾選，不是留空猜語意——主人裁示）
 * - 鍵缺席＝依 `absent_default`（null＝不限、0＝當 0）；本編輯器輸出一律**每鍵齊帶**
 * - `absent_default: 0` 的鍵（例如某產品的協作用配額）起手值是 0 而不是「無上限」
 *   （舊版靠寫死鍵名判斷，改資料驅動後讀憑證設計）
 *
 * ## 批114：三個旗標（`required`／`allow_unlimited`，第三個是 portal 用的）
 *
 * 批114 之前，「這個鍵必填、而且不可以無上限」是 LS 規則層寫死在某一個鍵上的內建特例，
 * 表單另外給它一個獨立的數字欄。現在改由**憑證設計宣告**，這支編輯器照著渲染：
 * - `allow_unlimited === false`：**不畫「無上限」勾選**；帶進來的 `null` 當成「未填」
 *   （顯示 0 卻送出 null，或反過來把非法值洗成 0，都比空著更難發現）。
 * - `required === true`：標「必填」；沒有值時該列紅字，而且**輸出的 map 裡不放這個鍵**
 *   ——不要拿 0／null 充數，缺鍵讓後端回 `quota_required` 才是誠實的。
 *   起手值也不套 `absent_default`：必填的意思就是「缺鍵不合法」，那個鍵沒有缺席語意。
 *
 * ## `update:valid`／`defineExpose({ valid })`
 *
 * 這份配額填得完不完整，只有本元件知道（父層看不到憑證設計的旗標）。父層拿它 disable
 * 送出鈕即可；**它不是權限閘**，後端 `_fold_quotas` 會再擋一次。
 * `spec` 還沒載回來（或這個產品沒宣告任何配額鍵）時一律 `true`——不知道規則就不擋人。
 * ★ 套裝編輯器（〈產品 → 套裝〉）**刻意不接這個事件**：套裝「不放某個鍵」是合法的
 *   （＝由操作者核發時自己填，企業級就是這樣），拿它擋存檔會讓企業級套裝存不起來。
 *
 * ★ `initFrom` 只在掛載時跑 → 父層帶入新值（套用套裝／**換產品**）一律換 `:key` 重掛。
 *
 * ## 批151：子證（加購）語境 `addonContext`
 *
 * 加購申請的配額是**子證**的值，與主證怎麼合併依每個鍵宣告的 `addon_merge`
 * （`lib/addonQuotaInput.ts`）：`sum`（加總）的鍵要填**增加量**，不是目標值。父層在加購語境
 * 傳 `addon-context`，這裡多畫 placeholder（「增加量」／「目標值」）與非預設合併方式的一行說明。
 *
 * ### 11-01：加購語境**只送有填正數的鍵**（修既有缺陷）
 *
 * 子證的規則是「鍵缺席＝不動這一項、`null`＝拒簽、值必須是正整數」，與主證的「每鍵齊帶、`null`＝
 * 無上限、缺鍵＝`absent_default`」**相反**。原本加購語境沿用主證的輸出（每鍵齊帶，沒填的鍵是 `null`
 * 或 `absent_default` 的 0），代理商從表單送出的加購一律在核發時被 `null_quota_value`／
 * `invalid_quota_value` 擋下。所以加購語境下：
 * - 起手一律空白（不套 `absent_default`、不勾無上限），帶進來的值只認正數；
 * - **不畫「無上限」勾選**（子證不能解除上限）、**不標必填**（必填是主證核發的規則）；
 * - 輸出只放**填了正數**的鍵；空白與 0＝不送（＝這張加購不動那一項）；未知鍵也只留正數。
 * 主證編輯（不傳）逐位元照舊。後端核發時照舊嚴格檢查。
 *
 * ## 加購數量級距（11-01 裁示，同樣只在 `addonContext`）
 *
 * 配額鍵宣告了 `addon_step` 時，輸入框的 `min`／`step` 改成一階，填了**正數卻不是倍數**就標紅、
 * `valid` 回 false（父層據此 disable 送出；後端 422 `addon_quota_step` 仍是唯一真相）。
 * 空白與 0 不在這裡擋（不送出，見上）。
 */
const props = defineProps<{
  modelValue: Record<string, number | null> | null
  /** 該產品的憑證設計；null＝尚未載入（顯示提示、原樣回吐 modelValue）。 */
  spec: ProductSpec | null
  /** 子證（加購）語境：依子證合併方式提示填法，而且**只輸出填了正數的鍵**（見檔頭）。 */
  addonContext?: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [Record<string, number | null>]
  /** 批114：必填鍵都有值、且沒有「不可無上限卻是 null」的鍵。 */
  'update:valid': [boolean]
}>()

const fields = computed(() => props.spec?.quotas ?? [])

/**
 * 內部狀態：每欄「數字草稿＋無上限旗標」。
 *
 * ★ `nums` 的值型別是 `string | number`：`<input type="number">` 的 v-model 會被 Vue
 *   自動轉成 number（批106 的 `.trim()` TypeError 就是這樣來的），清空時又變回 `''`。
 *   一律經 `rawOf()` 讀，不要在別處直接 `.trim()`。
 */
const nums = ref<Record<string, string | number>>({})
const unlimited = ref<Record<string, boolean>>({})
/**
 * 授權上有、但這個產品憑證設計沒有的配額鍵——**原樣保留**（同 ModulePicker 的理由：
 * 白名單重建靜默刪鍵＝客戶上限被無聲放寬／收緊，而畫面上完全看不出來）。
 */
const unknownEntries = ref<Record<string, number | null>>({})

/** 加購語境下「算數」的值：正數（其餘＝不送）。 */
function isPositive(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0
}

function initFrom(q: Record<string, number | null> | null) {
  const known = new Set<string>()
  for (const f of fields.value) {
    known.add(f.key)
    const allowUnl = quotaAllowsUnlimited(f)
    const v = q?.[f.key]
    if (props.addonContext) {
      // 子證：起手空白、只認帶進來的正數（null／0 是舊表單每鍵齊帶的殘留，不是使用者填的）。
      unlimited.value[f.key] = false
      nums.value[f.key] = isPositive(v) ? String(v) : ''
      continue
    }
    if (v === null) {
      // 明確的「不限」。★ 不准無上限的鍵沒有那個勾選框 → 只能當成**未填**，
      //   讓操作者重填一個數字（靜默改成 0 等於幫客戶決定上限）。
      unlimited.value[f.key] = allowUnl
      nums.value[f.key] = ''
    } else if (typeof v === 'number') {
      unlimited.value[f.key] = false
      nums.value[f.key] = String(v)
    } else if (quotaRequired(f)) {
      // 必填鍵沒有「缺席語意」（缺鍵＝422），所以不套 `absent_default`——起手就是
      // 未填、紅字提示。批114 之前這是那個獨立數字欄的「請先補…」提示，語意一模一樣。
      unlimited.value[f.key] = false
      nums.value[f.key] = ''
    } else {
      // 鍵缺席（舊授權／新表單）→ 依憑證設計的 `absent_default`：
      //   null＝不限（可無上限時勾起來）、數字＝那個數字。
      const d = f.absent_default
      unlimited.value[f.key] = d == null && allowUnl
      nums.value[f.key] = d == null ? '' : String(d)
    }
  }
  const unknown: Record<string, number | null> = {}
  for (const [k, v] of Object.entries(q ?? {})) {
    // 子證語境：未知鍵同樣只留正數（null／0 送出去只會被拒簽）。
    if (!known.has(k) && (!props.addonContext || isPositive(v))) unknown[k] = v
  }
  unknownEntries.value = unknown
}
initFrom(props.modelValue)
// spec 晚到時重新初始化（父層通常會 bump `:key`，這是保險）。
watch(
  () => props.spec,
  () => {
    initFrom(props.modelValue)
    emitAll()
  },
)
// 父層帶入（issueForHost／套用套裝）一律以 :key 重掛本元件 → 走 mounted。
// ★ 掛載即回寫一次（每鍵齊全）：否則使用者沒動任何欄位就送出時，父層還是帶入前的
//   原值（可能空 dict／鍵不全），與畫面顯示不同步（主人回報「企業級預設 0」的另一半根因）。
onMounted(() => emitAll())

/** 讀某欄的輸入草稿（`type="number"` 的 v-model 可能已經是 number，見 `nums`）。 */
function rawOf(key: string): string {
  const v = nums.value[key]
  return v == null ? '' : String(v).trim()
}

/**
 * 子證語境下「填了正數卻不是加購級距的倍數」的鍵 → 錯誤文字（見檔頭〈加購數量級距〉）。
 * 判準走 `addonStepError(allowUnset: true)`：空白／0／無上限放行。
 */
const stepIssues = computed<Record<string, string>>(() => {
  if (!props.addonContext) return {}
  const out: Record<string, string> = {}
  for (const f of fields.value) {
    if (unlimited.value[f.key]) continue
    const msg = addonStepError(f, rawOf(f.key), { allowUnset: true })
    if (msg) out[f.key] = msg
  }
  return out
})
/** 子證語境下這個鍵的級距（輸入框的 min／step）；非子證語境或沒宣告＝`null`。 */
function stepOf(f: QuotaSpec): number | null {
  return props.addonContext ? quotaAddonStep(f) : null
}

/**
 * 目前狀態算出來的輸出（＋哪些必填鍵還沒填）。
 *
 * ★ 寫成 computed 而不是在 `emitAll` 裡算：紅字提示要在**第一次 render 就正確**
 *   （`onMounted` 才寫 ref 的話，掛載當下那一幀是空的，測試與真人都看得到閃爍）。
 */
const built = computed(() => {
  const out: Record<string, number | null> = {}
  const gaps: string[] = []
  if (props.addonContext) {
    // 子證：只放填了正數的鍵（見檔頭〈只送有填正數的鍵〉）；沒有必填、沒有無上限。
    for (const f of fields.value) {
      const raw = rawOf(f.key)
      if (raw === '') continue
      const n = Number(raw)
      if (isPositive(n)) out[f.key] = n
    }
    return { out, gaps, ok: !Object.keys(stepIssues.value).length }
  }
  for (const f of fields.value) {
    if (unlimited.value[f.key]) {
      out[f.key] = null
      continue
    }
    const raw = rawOf(f.key)
    if (raw === '') {
      // 必填未填＝**不輸出這個鍵**（拿 0 或 null 充數會簽出一張沒人發現的錯證）；
      // 非必填留空沿用舊行為＝0。
      if (quotaRequired(f)) {
        gaps.push(f.key)
        continue
      }
      out[f.key] = 0
      continue
    }
    out[f.key] = Math.max(0, Number(raw) || 0)
  }
  // 「不可無上限卻是 null」：UI 上按不出來（那些列沒有勾選框），這裡是對著輸出再確認
  // 一次——判準與後端 `_fold_quotas` 的 `quota_not_nullable` 同一條。
  const nullable = fields.value.some((f) => out[f.key] === null && !quotaAllowsUnlimited(f))
  return { out, gaps, ok: !gaps.length && !nullable && !Object.keys(stepIssues.value).length }
})
/** 必填卻沒有值的鍵（畫面紅字）。 */
const missing = computed(() => built.value.gaps)
/** 這份配額填得完不完整；`defineExpose` 給父層直接讀（事件之外的第二條路）。 */
const valid = computed(() => built.value.ok)
defineExpose({ valid })

function emitAll() {
  // spec 還沒到＝不知道有哪些鍵 → 原樣回吐（絕不清空成 {}），也不擋人（`ok` 恆真）。
  if (!fields.value.length) emit('update:modelValue', { ...(props.modelValue ?? {}) })
  else emit('update:modelValue', { ...built.value.out, ...unknownEntries.value })
  emit('update:valid', built.value.ok)
}

function toggleUnlimited(key: string) {
  unlimited.value[key] = !unlimited.value[key]
  emitAll()
}

/** 必填標示只屬於主證核發；子證語境一律不標（加購沒有「必填」）。 */
function isRequired(f: QuotaSpec): boolean {
  return !props.addonContext && quotaRequired(f)
}

/** 子證語境下每個鍵的填法；非子證語境一律 `null`（不畫任何提示）。 */
const addonMeta = computed<Record<string, AddonQuotaInputMeta> | null>(() =>
  props.addonContext ? Object.fromEntries(fields.value.map((f) => [f.key, addonQuotaInputMeta(f)])) : null,
)
function placeholderOf(f: QuotaSpec): string {
  if (unlimited.value[f.key]) return '∞'
  // 子證：空白＝不送，placeholder 說的是「填了算什麼」（增加量／目標值），不是 0。
  const meta = addonMeta.value?.[f.key]
  if (meta) return meta.inputLabel
  return isRequired(f) ? '必填' : '0'
}
/** 「無上限」勾選：主證才有（子證不能解除上限，`null` 會被拒簽）。 */
function allowsUnlimited(f: QuotaSpec): boolean {
  return !props.addonContext && quotaAllowsUnlimited(f)
}
</script>

<template>
  <div>
    <p v-if="!fields.length" class="qe-empty">
      這個產品還沒有定義任何配額——請先到〈產品 → 憑證設計〉新增配額鍵。
    </p>
    <!-- 11-01：子證只送填了數字的項目（固定一行，不隨輸入跳動版面）。 -->
    <p v-else-if="addonContext" class="qe-empty">只送出有填數字的項目；留空＝這張加購不動那一項。</p>
    <div class="qe">
      <div
        v-for="f in fields"
        :key="f.key"
        class="qe-row"
        :class="{ dim: f.deprecated, gap: missing.includes(f.key) || !!stepIssues[f.key] }"
      >
        <div class="qe-main">
          <span class="qe-label" :title="f.hint ?? f.key">
            {{ f.label
            }}<small v-if="isRequired(f)" class="req" title="簽發時一定要有值">＊必填</small>
            <small v-if="f.deprecated" class="dep">（已停用）</small>
          </span>
          <!-- 子證語境＋宣告了加購級距：min／step 都是一階（瀏覽器的上下鍵一次跳一階）。 -->
          <input
            v-model="nums[f.key]"
            type="number"
            :min="stepOf(f) ?? 0"
            :step="stepOf(f) ?? undefined"
            class="qe-num"
            :disabled="unlimited[f.key]"
            :placeholder="placeholderOf(f)"
            @input="emitAll"
          />
          <!-- 憑證設計宣告「不可無上限」的鍵不給這個勾選（批114）。 -->
          <label v-if="allowsUnlimited(f)" class="qe-unl" :class="{ on: unlimited[f.key] }">
            <input type="checkbox" :checked="unlimited[f.key]" @change="toggleUnlimited(f.key)" />
            <span>無上限</span>
          </label>
        </div>
        <!-- 批114：按下去之前就講，而不是讓人按了才看到伺服器回 `quota_required`。 -->
        <small v-if="missing.includes(f.key)" class="qe-gap">請先填「{{ f.label }}」</small>
        <small v-else-if="stepIssues[f.key]" class="qe-gap">「{{ f.label }}」{{ stepIssues[f.key] }}</small>
        <!-- 批151：子證語境才有——非預設合併方式的填法（加總＝填增加量）。 -->
        <small v-if="addonMeta?.[f.key]?.note" class="qe-merge">{{ addonMeta?.[f.key]?.note }}</small>
      </div>
    </div>
    <p v-if="Object.keys(unknownEntries).length" class="qe-unknown">
      ⚠ 這張授權含本產品憑證設計沒有的配額代號：
      <code v-for="(v, k) in unknownEntries" :key="k" class="mono">{{ k }}={{ v ?? '不限' }}</code>
      。已<strong>原樣保留</strong>。
    </p>
  </div>
</template>

<style scoped>
.qe {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--sp-2) var(--sp-4);
}
.qe-empty {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.qe-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
/**
 * 一列三格：**名稱 / 數字框 / 無上限**。
 *
 * ★★ 刻意用 grid 而不是 flex（批121 修）：`allow_unlimited: false` 的鍵（例如席次）
 *    **沒有「無上限」勾選框**，flex 版的 `.qe-label { flex: 1 }` 會讓它吃掉那一格的寬度，
 *    數字框被推到最右 ⇒ 同一欄裡只有那一列的框右移約 70px，還會撞上右欄的名稱。
 *    第三欄固定寬、沒有勾選框時就留空，每一列的數字框位置才會一致。
 */
.qe-main {
  display: grid;
  grid-template-columns: 1fr 84px 72px;
  align-items: center;
  gap: var(--sp-2);
}
.qe-row.dim .qe-label {
  color: var(--fg-4);
}
.qe-row.gap .qe-num {
  border-color: var(--danger);
}
.qe-label .req {
  margin-left: 2px;
  color: var(--danger);
  font-size: var(--fs-11);
}
.qe-gap {
  color: var(--danger);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.qe-merge {
  color: var(--fg-4);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.qe-label {
  /* grid 第一欄（`1fr`）；`min-width: 0` 才截得斷——沒有它，長名稱會把整欄撐寬、
     把數字框擠出格線（憑證設計的名稱由各產品自訂，長度不可控）。 */
  min-width: 0;
  font-size: var(--fs-13);
  color: var(--fg-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.qe-label .dep {
  color: var(--warning);
  font-size: var(--fs-11);
}
.qe-num {
  width: 84px;
  text-align: right;
}
.qe-num:disabled {
  opacity: 0.45;
}
.qe-unl {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-12);
  color: var(--fg-4);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.qe-unl.on {
  color: var(--brand-600, var(--fg-2));
  font-weight: 600;
}
.qe-unknown {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-11);
  color: var(--warning);
  line-height: var(--lh-base);
}
.qe-unknown code {
  margin: 0 3px;
}
</style>
